import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StudentLayout from '../components/student/StudentLayout';
import knowledge from '../img/ModuleImg.jpg';
import { Typography, Modal, Alert, Snackbar } from '@mui/material';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import '@react-pdf-viewer/core/lib/styles/index.css';
import StudentUpcomingMeetingsModal from '../components/StudentUpcomingMeetingsModal';
import { useTranslation } from 'react-i18next';
import { getRoleLabels, getStoredTutorType } from '../utils/roleTerminology';
import StudentClassContent from '../components/student/StudentClassContent';

function StudentClass() {

  const { t } = useTranslation();
  const role = getRoleLabels(t, getStoredTutorType());
  const [studentUnreadChats, setStudentUnreadChats] = useState(0);
  const [upcomingMeeting, setUpcomingMeeting] = useState(null);
  const [upcomingMeetingsList, setUpcomingMeetingsList] = useState([]);
  const [isUpcomingModalOpen, setIsUpcomingModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  const [allCategoriesOpen, setAllCategoriesOpen] = useState(false);
  const [openCategories, setOpenCategories] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(t("ستكون هذه الميزة متاحة قريبًا"));
  const { groupId } = useParams();
  const navigate = useNavigate();
  const auth = React.useMemo(() => getAuth(), []);
  const [error, setError] = useState('');
  const [groupName, setGroupName] = useState('');
  const [groupImage, setGroupImage] = useState('');
  const QUIZ_STUDENT_LOGIN_URL =
  process.env.REACT_APP_QUIZ_STUDENT_LOGIN_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:3001/studentLogin' 
    : 'https://darssy-quizzes.web.app/studentLogin'); 


  useEffect(() => {
    let unsubA = null; // conversations where studentId == auth.uid
    let unsubB = null; // conversations where studentId == studentDocId (students collection)
    let unsubC = null; // conversations where studentUid == auth.uid (legacy/alternate schema)

    const computeTotal = (snapshots) => {
      // Merge docs by conversation doc id to avoid double counting
      const merged = new Map();

      snapshots.forEach((snap) => {
        if (!snap) return;
        snap.forEach((docSnap) => {
          merged.set(docSnap.id, docSnap.data() || {});
        });
      });

      let total = 0;
      merged.forEach((data) => {
        total += Number(data.unreadCountStudent || 0);
      });

      setStudentUnreadChats(total);
    };

    // Hold latest snapshots
    let lastA = null;
    let lastB = null;
    let lastC = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      // cleanup old listeners
      if (unsubA) unsubA();
      if (unsubB) unsubB();
      if (unsubC) unsubC();
      unsubA = unsubB = unsubC = null;
      lastA = lastB = lastC = null;

      if (!user) {
        setStudentUnreadChats(0);
        return;
      }

      // (1) Listen by studentId == auth.uid (some schemas do this)
      unsubA = onSnapshot(
        query(collection(db, "conversations"), where("studentId", "==", user.uid)),
        (snap) => {
          lastA = snap;
          computeTotal([lastA, lastB, lastC]);
        },
        (err) => {
          console.error("Unread listener A error:", err);
          lastA = null;
          computeTotal([lastA, lastB, lastC]);
        }
      );

      // (2) Listen by studentUid == auth.uid (alternate field name)
      unsubC = onSnapshot(
        query(collection(db, "conversations"), where("studentUid", "==", user.uid)),
        (snap) => {
          lastC = snap;
          computeTotal([lastA, lastB, lastC]);
        },
        (err) => {
          console.error("Unread listener C error:", err);
          lastC = null;
          computeTotal([lastA, lastB, lastC]);
        }
      );

      // (3) Resolve student document id, then listen by studentId == studentDocId
      try {
        const studentQ = query(collection(db, "students"), where("uid", "==", user.uid));
        const studentSnap = await getDocs(studentQ);

        if (!studentSnap.empty) {
          const studentDocId = studentSnap.docs[0].id;

          unsubB = onSnapshot(
            query(collection(db, "conversations"), where("studentId", "==", studentDocId)),
            (snap) => {
              lastB = snap;
              computeTotal([lastA, lastB, lastC]);
            },
            (err) => {
              console.error("Unread listener B error:", err);
              lastB = null;
              computeTotal([lastA, lastB, lastC]);
            }
          );
        } else {
          // no student doc found -> just compute from A/C
          computeTotal([lastA, lastB, lastC]);
        }
      } catch (e) {
        console.error("Failed to resolve student doc id:", e);
        computeTotal([lastA, lastB, lastC]);
      }
    });

    return () => {
      if (unsubA) unsubA();
      if (unsubB) unsubB();
      if (unsubC) unsubC();
      unsubscribeAuth();
    };
  }, [auth]);

  // Parse "YYYY/MM/DD" + "HH:MM" -> timestamp (ms)
  const toStartMillis = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const [y, m, d] = dateStr.split('/').map(Number);
    const [hh, mm] = timeStr.split(':').map(Number);
    return new Date(y, m - 1, d, hh, mm, 0, 0).getTime();
  };

  // Show remaining time in d/h/m
  const formatStartsIn = (targetMs) => {
    if (!targetMs) return '';
    const diffMs = targetMs - Date.now();
    if (diffMs <= 0) return t('Now');

    const totalMinutes = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    let result = '';
    if (days > 0) result += `${days}d `;
    if (hours > 0 || days > 0) result += `${hours}h `;
    result += `${minutes}m`;
    return result.trim();
  };

  // Open link in new tab; add https:// if missing
  const openMeetingLink = (url) => {
    if (!url) return;
    const fullUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };


  const fetchUpcomingMeeting = useCallback(async () => {
    try {
      // 1) Get group data to try to find teacher UID
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);
      const gdata = groupSnap.exists() ? groupSnap.data() : null;

      setGroupName(gdata?.groupName || gdata?.name || gdata?.title || '');
      setGroupImage(gdata?.groupImage || '');

      let teacherUid = gdata?.teacherUid || null;

      // 2) Fallback: find teacher by group membership, then read uid
      if (!teacherUid) {
        const tq = query(
          collection(db, 'teachers'),
          where('groupIds', 'array-contains', groupId)
        );
        const tSnap = await getDocs(tq);
        if (!tSnap.empty) {
          const tDoc = tSnap.docs[0];
          teacherUid = tDoc.data()?.uid || tDoc.id;
        }
      }

      if (!teacherUid) {
        console.warn('No teacher UID found for this group.');
        setUpcomingMeeting(null);
        setUpcomingMeetingsList([]);
        return;
      }

      // 3) Read meetings from new path: meetings/<teacherUid>/<groupId>/<autoDocId>
      const mtgSnap = await getDocs(collection(db, 'meetings', teacherUid, groupId));
      const items = [];
      mtgSnap.forEach((d) => {
        const data = d.data();
        const startMs = toStartMillis(data?.date, data?.startTime);
        items.push({ id: d.id, ...data, startMs });
      });

      const now = Date.now();
      const upcomingList = items
        .filter((m) => m.startMs && m.startMs >= now)
        .sort((a, b) => a.startMs - b.startMs);

      setUpcomingMeeting(upcomingList[0] || null);   // next meeting
      setUpcomingMeetingsList(upcomingList);         // all upcoming meetings
    } catch (e) {
      console.error('Failed to fetch teacher upcoming meeting:', e);
      setUpcomingMeeting(null);
      setUpcomingMeetingsList([]);
    }
  }, [groupId, t]);


  const verifyPermissions = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      setError(t('You must be logged in to access this page.'));
      navigate('/student/login');
      return false;
    }
  
    const studentQuery = query(
      collection(db, 'students'),
      where('uid', '==', user.uid)
    );
    const studentDocs = await getDocs(studentQuery);
  
    if (
      studentDocs.empty || 
      !Array.isArray(studentDocs.docs[0]?.data()?.enrollments) || 
      !studentDocs.docs[0].data().enrollments.some((enrollment) => enrollment?.groupId === groupId)
    ) {
      setError(t('You do not have access to this group.'));
      navigate('/student/classes');
      return false;
    }
    
    return true;
  }, [auth, groupId, navigate, t]);

  

  const fetchFiles = useCallback(async () => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const filesSnapshot = await getDocs(collection(groupRef, 'files'));
  
      const fetchedFiles = [];
      filesSnapshot.forEach((doc) => {
        const fileData = { id: doc.id, ...doc.data() };

        // Assign default type if it doesn't exist
        if (!fileData.type) {
          fileData.type = 'exercises';
        }
  
        fetchedFiles.push(fileData);
      });
  
      fetchedFiles.sort((a, b) => b.uploadedAt - a.uploadedAt);
  
      const grouped = fetchedFiles.reduce((acc, file) => {
        const category = file.category || t('Uncategorized');
        if (!acc[category]) acc[category] = [];
        acc[category].push(file);
        return acc;
      }, {});
  
      setCategories(Object.entries(grouped));
      // initially: all folders closed
      setOpenCategories(
        Object.fromEntries(Object.keys(grouped).map((key) => [key, false]))
      );
      setAllCategoriesOpen(false);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError(t('حدث خطأ أثناء جلب الملفات'));
    }
  }, [groupId, t]);


  useEffect(() => {
    const initialize = async () => {
      const hasPermission = await verifyPermissions();
      if (hasPermission) {
        fetchFiles();
        fetchUpcomingMeeting();   // <- add this
      }
    };
    initialize();
  }, [verifyPermissions, fetchFiles, fetchUpcomingMeeting]);


  const handleFileClick = (file) => {
      if (!file || !file.url) {
          console.error('File URL is not valid');
          return;
      }

      navigate(`/student/class/${groupId}/lesson`, {
        state: { fileUrl: file.url, fileType: file.type } 
      });
  };


    const handleToggleAllCategories = () => {
      setAllCategoriesOpen((prev) => {
        const next = !prev; // true = open all, false = close all
        setOpenCategories((prevOpen) =>
          Object.fromEntries(Object.keys(prevOpen).map((key) => [key, next]))
        );
        return next;
      });
    };

    const handleQuizButtonClick = async () => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);

      if (!groupSnap.exists()) {
        setSnackbarMessage(t('حدث خطأ في تحميل بيانات القسم. يرجى المحاولة لاحقًا.'));
        setOpenSnackbar(true);
        return;
      }

      const data = groupSnap.data();
      const teacherQuizCode = data?.TeacherQuizCode;
      const studentQuizCode = data?.StudentQuizCode;

      // Teacher must have set BOTH codes
      if (!teacherQuizCode || !studentQuizCode) {
        setSnackbarMessage(t('الأستاذ لم يفعل بعد خاصية الإستجوابات لهذه المجموعة.'));
        setOpenSnackbar(true);
        return;
      }

      // Redirect to Darssy Quizzes student login with the studentQuizCode
      const url = `${QUIZ_STUDENT_LOGIN_URL}?code=${encodeURIComponent(
        studentQuizCode
      )}&groupId=${encodeURIComponent(groupId)}`;

      window.location.href = url;
    } catch (err) {
      console.error('Error checking quiz codes for student:', err);
      setSnackbarMessage(t('حدث خطأ أثناء التحقق من بيانات الإستجوابات. حاول مرة أخرى.'));
      setOpenSnackbar(true);
    }
  };


  const filterFilesByType = (files) =>
    files.filter(
      (file) =>
        file.type === 'file' ||
        file.type === 'video' ||
        file.type === 'audio' ||
        file.type === 'image' ||
        file.type === 'swf' ||
        file.type === 'link'
    );

  const handleCategoryClick = (category) => {
    setOpenCategories((prev) => {
      const isOpen = !!prev[category];

      // If it's already open, close it (all closed)
      if (isOpen) {
        const newState = { ...prev, [category]: false };
        setAllCategoriesOpen(false);
        return newState;
      }

      // Open ONLY this category, close others
      const newState = Object.fromEntries(
        Object.keys(prev).map((key) => [key, key === category])
      );
      setAllCategoriesOpen(false); // no longer in "open all" mode
      return newState;
    });
  };


  return (
    <StudentLayout>
      <StudentClassContent
        role={role}
        groupName={groupName}
        groupImage={groupImage || knowledge}
        categories={categories}
        openCategories={openCategories}
        allCategoriesOpen={allCategoriesOpen}
        upcomingMeeting={upcomingMeeting}
        studentUnreadChats={studentUnreadChats}
        filterFilesByType={filterFilesByType}
        formatStartsIn={formatStartsIn}
        onToggleCategory={handleCategoryClick}
        onToggleAll={handleToggleAllCategories}
        onOpenFile={(file) => {
          if (file.type === 'link') {
            const url = file.url || '';
            const fullUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
            window.open(fullUrl, '_blank', 'noopener,noreferrer');
          } else {
            handleFileClick(file);
          }
        }}
        onMeetings={() => setIsUpcomingModalOpen(true)}
        onChat={() => navigate('/student/chat')}
        onQuiz={handleQuizButtonClick}
        onExercises={() => navigate(`/student/class/${groupId}/exercises`)}
        onClasses={() => navigate('/student/classes')}
        onJoinMeeting={() => openMeetingLink(upcomingMeeting?.url)}
      />

      <Modal
        open={isPdfViewerOpen}
        onClose={() => setIsPdfViewerOpen(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: '100vw',
            height: '100vh',
            backgroundColor: '#fff',
            overflow: 'hidden',
          }}
        >
          {selectedFileUrl ? (
            <iframe
              src={selectedFileUrl}
              title={t('PDF Viewer')}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          ) : (
            <Typography variant="h6">{t('Loading PDF...')}</Typography>
          )}
        </div>
      </Modal>

      {/* Snackbar for COMING SOON message */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        sx={{ display: 'flex', justifyContent: 'center' }}
      >
        <Alert
            onClose={() => setOpenSnackbar(false)}
            severity="info"
            sx={{ width: '100%', fontSize: '18px' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <StudentUpcomingMeetingsModal
        open={isUpcomingModalOpen}
        onClose={() => setIsUpcomingModalOpen(false)}
        meetings={upcomingMeetingsList}
      />
    </StudentLayout>
  );
}

export default StudentClass;
